/** biome-ignore-all lint/suspicious/noConsole: we don't have logger here*/

import { buildContactoCountryData } from '@shared/utils'
import { getCountryCodeFromIP } from '@shared/utils/country-detection'
import { cn } from 'lib/utils'
import parsePhoneNumberFromString, {
  AsYouType,
  type CountryCode,
  getCountryCallingCode,
  Metadata,
} from 'libphonenumber-js'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Flag } from '../icon'
import { Input } from '../input'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import CountryDropdown from './country-dropdown'

// @ts-expect-error - accessing internal metadata property
const dialCodeMap = new Metadata()?.metadata?.country_calling_codes as Record<string, string[]>

window.contactoCountryData = buildContactoCountryData()

const stripSpecialChars = (
  a: string | null | undefined,
  b: string | null | undefined,
): string | undefined => {
  const regex = /[(-)\s+]/g
  const str = a?.replace(regex, '')
  if ((str?.length ?? 0) < 6 && str === b?.replace(regex, ''))
    return str?.substring(0, str.length - 1)
  return str
}

interface PhoneNumberInputProps {
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  className?: string
  flagURL?: string
  disabled?: boolean
  placeholder?: string
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  allowText?: boolean
  label?: string
  autoDetectCountry?: boolean
  refocusOnFormat?: boolean
  /** When false, hides the country flag dropdown. Defaults to true. */
  showFlag?: boolean
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  className,
  flagURL,
  disabled,
  placeholder,
  allowText,
  autoDetectCountry = true,
  refocusOnFormat = true,
  showFlag = true,
  ...props
}) => {
  const ref = useRef<string | null>(null)
  const textFieldRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [number, setNumber] = useState(value)
  const countryRef = useRef<string | null>(null)
  const [hasAutoDetected, setHasAutoDetected] = useState(false)

  const handleCountryDetection = useCallback(async () => {
    if (!autoDetectCountry || value || hasAutoDetected) return

    try {
      const detectedCountry = await getCountryCodeFromIP()
      if (detectedCountry && window.contactoCountryData) {
        const countryData = window.contactoCountryData.find(([, iso]) => iso === detectedCountry)
        if (countryData) {
          const countryCallingCode = getCountryCallingCode(detectedCountry as CountryCode)
          const initialValue = `+${countryCallingCode} `

          countryRef.current = detectedCountry
          setNumber(initialValue)
          onChange({ target: { value: initialValue } } as React.ChangeEvent<HTMLInputElement>)
          setHasAutoDetected(true)
        }
      }
    } catch (error) {
      console.error('Failed to auto-detect country:', error)
    }
  }, [autoDetectCountry, value, hasAutoDetected, onChange])

  // Auto-detect country from IP on component mount
  useEffect(() => {
    handleCountryDetection()
  }, [handleCountryDetection])

  useEffect(() => {
    if (allowText && value?.[0]?.match(/[a-zA-Z]/)) return setNumber(value)
    let newValue = stripSpecialChars(value, ref.current)
    const countryFromDialMap = dialCodeMap[newValue || '']?.[0]
    countryRef.current = countryFromDialMap || countryRef.current
    if (newValue && newValue[0] !== '+') newValue = `+${newValue}`
    let finalNumber = newValue
    if ((finalNumber?.length ?? 0) < 1) countryRef.current = null
    if (newValue && newValue.length > 3) {
      const a = new AsYouType(countryRef.current as CountryCode | undefined)
      a.input(newValue)
      countryRef.current = a.getCountry() || countryRef.current
      const nationalNumber = a.getNationalNumber()
      const b = newValue.replace(/[-]/g, '')
      const ext = parsePhoneNumberFromString(b)?.ext
      const extWithHash = b.at(-1) === '#' ? '#' : ext ? `#${ext}` : ''

      finalNumber =
        newValue &&
        `+${a.getCallingCode() || ''} ${new AsYouType(
          countryRef.current as CountryCode | undefined,
        ).input(nationalNumber)}${extWithHash || ''}`
    }
    ref.current = finalNumber || null
    setNumber(finalNumber || '')
    if (refocusOnFormat && textFieldRef.current) {
      textFieldRef.current.focus()
    }
  }, [value, allowText])

  return (
    <div ref={wrapperRef}>
      <Input
        ref={textFieldRef}
        {...props}
        placeholder={placeholder}
        value={number}
        className={cn(className, showFlag && 'pl-0')}
        disabled={disabled}
        inputClassName={showFlag ? 'pl-9' : undefined}
        {...(showFlag && {
          startIcon: () => (
            <Popover modal open={visible && !disabled} onOpenChange={setVisible}>
              <PopoverTrigger asChild className={disabled ? 'pointer-events-none' : ''}>
                <span className="cursor-pointer flex items-center" tabIndex={-1}>
                  <Flag
                    url="/flags.svg"
                    iso={countryRef.current || 'unknown'}
                    circle
                    size={20}
                    className={disabled ? 'opacity-50' : ''}
                  />
                </span>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-2">
                <CountryDropdown
                  visible={visible}
                  setVisible={setVisible}
                  onChange={onChange}
                  textFieldRef={textFieldRef}
                  countryISO={countryRef}
                  value={number}
                />
              </PopoverContent>
            </Popover>
          ),
        })}
        onChange={({ target: { value: v } }) => {
          // Pasted text can carry tabs, odd spaces or a leading space.
          v = v.replace(/\s+/g, ' ').trimStart()
          if (allowText && v?.[0]?.match(/[a-zA-Z]/)) {
            onChange({ target: { value: v } } as React.ChangeEvent<HTMLInputElement>)
            return
          }
          let ext = ''
          if (v.length > 8) {
            const a = new AsYouType()
            // Check format against a '+'-prefixed value (matching the display logic
            // below) so a pasted raw national number isn't wrongly flagged as invalid
            // and truncated. Without the '+', a complete number like "13439617685" has
            // no matching format and loses its last digit. The value passed to the
            // parent (`cleaned`) is unchanged — only the validity probe is normalized.
            a.input(v.trim().startsWith('+') ? v : `+${v.replace(/\D/g, '')}`)
            // @ts-expect-error - matchingFormats is a private property
            const isValidFormat = !!a.formatter.matchingFormats.length
            if (!isValidFormat) v = v.substring(0, v.length - 1)
            ext = parsePhoneNumberFromString(v)?.ext || ''
          }
          if (ext?.length < 9) {
            const cleaned = v.replace(/[^+()-\d#\s]/g, '')
            if (cleaned === number) {
              if (textFieldRef.current) textFieldRef.current.value = number
              return
            }
            const syntheticEvent = {
              target: {
                value: cleaned,
              },
            } as React.ChangeEvent<HTMLInputElement>
            onChange(syntheticEvent)
          }
        }}
      />
    </div>
  )
}
